import axios, { AxiosError, AxiosRequestConfig } from 'axios'
import { useSessionModule } from '@/modules/session'
import MockAdapter from 'axios-mock-adapter'
import config from '@/modules/_shared/config'
import fileDownload from 'js-file-download'

export const apiUrl = (url: string): string => `${config.midgardUrlPanel}/panel/${url}`

export const mockAdapter = new MockAdapter(axios, {
    onNoMatch: 'passthrough'
})

async function getFromApi<Rres> (url: string): Promise<Rres> {
    try {
        const response = await axios.get<Rres>(apiUrl(url), authenticationHeader())
        return response.data
    } catch (error) {
        if (axios.isAxiosError(error)) {
            handleAxiosError(error)
        }

        throw new Error('unexpected error')
    }
}

function getDownloadFromApi (url: string, filename: string): void {
    axios.get(apiUrl(url), {
        ...authenticationHeader(),
        responseType: 'blob'
    }).then(res => {
        fileDownload(res.data, filename)
    })
}

async function uploadFileToApi<Rres> (url: string, rReq: unknown): Promise<Rres> {
    try {
        const response = await axios.post<Rres>(apiUrl(url), rReq,
            {
                ...authenticationHeader(),
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            }
        )
        return response.data
    } catch (error) {
        if (axios.isAxiosError(error)) {
            handleAxiosError(error)
        }

        throw new Error('unexpected error')
    }
}

function putToApi<Rres> (url: string, rReq: unknown): Promise<Rres>
function putToApi<Rres> (url: string): Promise<Rres>
async function putToApi<Rres> (url: string, rReq: unknown = null): Promise<Rres> {
    try {
        const response = await axios.put<Rres>(apiUrl(url), rReq, authenticationHeader())
        return response.data
    } catch (error) {
        if (axios.isAxiosError(error)) {
            handleAxiosError(error)
        }

        throw new Error('unexpected error')
    }
}

async function postToApi<Rres> (url: string, rReq: unknown): Promise<Rres> {
    try {
        const response = await axios.post<Rres>(apiUrl(url), rReq, authenticationHeader())
        return response.data
    } catch (error) {
        if (axios.isAxiosError(error)) {
            handleAxiosError(error)
        }

        throw new Error('unexpected error')
    }
}

function deleteInApi<Rres>(url: string, rReq: unknown): Promise<Rres>
function deleteInApi<Rres>(url: string): Promise<Rres>
async function deleteInApi<Rres> (url: string, rReq: unknown = null): Promise<Rres> {
    try {
        const response = await axios.delete<Rres>(apiUrl(url), {
            ...authenticationHeader(),
            data: rReq
        })
        return response.data
    } catch (error) {
        if (axios.isAxiosError(error)) {
            handleAxiosError(error)
        }
        throw new Error('unexpected error')
    }
}

function patchToApi<Rres> (url: string, rReq: unknown): Promise<Rres>
function patchToApi<Rres> (url: string): Promise<Rres>
async function patchToApi<Rres> (url: string, rReq: unknown = null): Promise<Rres> {
    try {
        const response = await axios.patch<Rres>(apiUrl(url), rReq, authenticationHeader())
        return response.data
    } catch (error) {
        if (axios.isAxiosError(error)) {
            handleAxiosError(error)
        }
        throw new Error('Unexpected error')
    }
}

function authenticationHeader (config: AxiosRequestConfig = axios.defaults): AxiosRequestConfig {
    const token = window.localStorage.getItem('token')
    if (token) {
        if (!config.headers) {
            config.headers = {}
        }

        config.headers.Authorization = `Bearer ${token}`
    }
    return config
}

type MidgardResponseError = {
    errors: {
        message: string,
        code?: string,
        path?: string
    }[]
}
const getApiErrorMessage = (error: MidgardResponseError) => {
    if (error.errors.length === 0) return 'something unknown happened'
    const firstError = error.errors[0]
    return firstError.message + `${firstError.path ? ` Path: "${firstError.path}"` : ''}`
}

// eslint-disable-next-line handle-callback-err
function handleAxiosError (error: AxiosError): void {
    const response = error.response
    if (response && response.status === 401) {
        const { logOut } = useSessionModule()
        logOut()
    } else {
        if (response && ((response.status === 400) || (response.status > 401))) {
            const errStr = `code: ${response.status}`
            if (typeof response.data === 'string') {
                throw new Error(`${errStr}, message: ${response.data}`)
            }
            if (response.data.errors && typeof response.data.errors[0].message === 'string') {
                throw new Error(`${errStr}, message: ${getApiErrorMessage(response.data)}`)
            }
        } else {
            throw new Error('axios error')
        }
    }
}

export function useAxiosModule () {
    return {
        getFromApi,
        putToApi,
        postToApi,
        deleteInApi,
        patchToApi,
        getDownloadFromApi,
        uploadFileToApi,
        mockAdapter,
        apiUrl
    }
}
